import { enqueueSnackbar } from "notistack"
import { api } from "../../api"
import { HistoryData, IFile, TicketDataResponseType } from "../Common/commonTypes"
import type { LogisticsTabData, LogisticsTabsResponse } from "../Logistics/LogisticsServices"

const MODULE_URL = `dispatch_request`

export type DispatchRequestPocPayload = { name?: string; phone?: string; email?: string }

export type RaiseDispatchRequestSoloPayload = {
    template_id: number
    project_name?: string
    visit_by_date?: string
    visit_by_time?: number
    source_site_id?: number | null
    destination_site_id?: number | null
    accounts?: {
        account_id: number
        account_type_id: number
        billing_address_id?: number
        contact_id?: number
        contact_type_id?: number
    }[]
    purchase_order?: number
    total_amount?: number | string
    category_id?: number
    contains_rfid?: boolean
    source_poc?: DispatchRequestPocPayload
    destination_poc?: DispatchRequestPocPayload
    chargers?: {
        product_id: number | undefined
        branding_id?: number
        operational_power_rating?: number
        remarks?: string
    }[]
    materials?: { product_id: number | undefined; quantity?: number; remarks?: string }[]
}

export type RaiseDispatchRequestSoloResponse = {
    status: string
    ticket_id: number
    project_id: number
    finance_id?: number
    add_payment_terms?: boolean
}

export type DispatchRequestSiteAddress = {
    id: number
    name: string
    address: string
    address_id?: number
    area?: string
    landmark?: string
    street?: string | null
    city_id: number
    city_name?: string
    state_id: number
    state_name?: string
    country_id?: number
    pincode?: string
    latitude?: number
    longitude?: number
    location_type?: string
    location_subtype?: string
    is_warehouse?: boolean | number
    poc: {
        name: string
        phone: string | null
        email?: string | null
        ecm_id?: number
        contact_id?: number
        contact_type_id?: number
    } | null
}

export type DispatchRequestReceivablePoItem = {
    sno: number
    sku_code: string
    quantity: number
    rate: string
    discount: string
    discount_unit: string
    tax: string
    total_amount: string
    delivered_quantity: number
    remaining_quantity: number
}

export type DispatchRequestReceivablePo = {
    po_id: number
    po_number: string
    po_name: string
    po_date?: string
    po_receive_date?: string
    status: string
    remarks?: string | null
    shipping_charges?: string
    total_po_value?: string
    account: { id: number; name: string }
    billing_address?: {
        id?: number
        address?: string
        city?: string
        state?: string
        pincode?: string
        gstin?: string
    }
    items: DispatchRequestReceivablePoItem[]
    po_file?: {
        doc_id: number | null
        doc_type: string
        file_name: string
        is_excel: boolean
        is_image: boolean
        name: string
        urls: { download: string; view: string }
    } | null
}

export type DispatchRequestProgressStep = {
    status_id: number
    sequence: number
    label: string
    is_marked: boolean
    completed_date: string | null
    is_skipped?: boolean
}

export type DispatchRequestProgress = {
    progress_percentage: number
    steps: DispatchRequestProgressStep[]
    history?: any[]
}

export type DispatchRequestOnHoldHistoryEntry = {
    s_no: number
    done_by: { id: number; name: string } | null
    on_hold_from: string | null
    on_hold_to: string | null
    duration_display: string
    duration_seconds: number
    reason?: string | null
    standby?: string | null
    status: string
}

export type DispatchRequestOnHoldHistory = {
    entries: DispatchRequestOnHoldHistoryEntry[]
    total_duration_display: string
    total_duration_seconds: number
}

export type DispatchRequestStakeholderContact = {
    contact_id: number
    name: string
    email?: string | null
    phone?: string | null
    contact_type?: string | null
    is_primary?: number
}

export type DispatchRequestStakeholderAccount = {
    account_id: number
    account_name: string
    account_type?: string | null
    contacts: DispatchRequestStakeholderContact[]
}

export type DispatchRequestStakeholders = {
    accounts: DispatchRequestStakeholderAccount[]
    site_accounts?: { site_id: number; site_name: string } | null
}

export type DispatchRequestDetailResponse = {
    dispatch_request: {
        id: number
        display_id: string
        source: string
        type: "linked" | "solo"
        parent_dr_id: number | null
        is_solo: boolean
        requested_delivery_date: string | null
        requested_delivery_time: string | null
        ecom_order_id?: string | number | null
        created_at: string
        updated_at: string
        created_by: { id: number; name: string } | null
    }
    project: {
        id: number
        name: string
        code: string
        deal_type?: string
        deal_value: number | null
        deal_value_is_exact: boolean
        close_date: string | null
        template: string | null
        project_type: string | null
        category?: { id: number; name: string } | null
        executed_by: { id: number; name: string } | string | null
        program?: string | { id?: number; program_id?: number; name?: string; program_name?: string } | null
        business_poc?: { id: number; name: string; email?: string; phone?: string } | null
        tags: {
            primary: { id: number; name: string; color_code?: string; text_color_code?: string; is_primary?: number }[]
            others: { id: number; name: string; color_code?: string; text_color_code?: string; is_primary?: number }[]
        }
    }
    account: { id: number; name: string; account_type?: string } | null
    additional_accounts_count?: number
    executed_by?: string | null
    executed_by_id?: number | null
    addresses: {
        source: DispatchRequestSiteAddress
        destination: DispatchRequestSiteAddress
        route: "intra_state" | "inter_state" | null
    }
    status: {
        bucket_key: string
        bucket_label: string
        child_key: string
        child_status: string
        color_code: string
        status_id: number
        stepper_label: string
        stepper_step: number
        stepper_total_steps: number
    }
    assigned_to: number | { id: number; name: string } | string | null
    material_allocation: any[]
    requestor_confirmation: {
        required: boolean
        reason?: string
        notified: { role: string; name: string | null }[]
        items?: any[]
        actions?: string[]
    }
    document_routing_preview: { commitment_doc: string; physical_doc: string; note: string } | null
    on_hold?: {
        on_hold: boolean
        reason?: string | null
        standby?: string | null
        tentative_date?: string | null
    }
    on_hold_history?: DispatchRequestOnHoldHistory
    purchase_order?: DispatchRequestReceivablePo[]
    progress?: DispatchRequestProgress
    stakeholders?: DispatchRequestStakeholders
}

export type DispatchRequestDownloadResponse = {
    success: boolean
    message?: string
    link: IFile | null
}

export type DispatchRequestOtherTicket = {
    ticket_id: number
    display_id: string
    module: string
    module_id: number
    submodule: string | null
    submodule_id: number | null
    assigned_to: string | null
    created_on?: string | null
    completed_on?: string | null
    progress_percentage: number
    status: string
    color_code?: string | null
    text_color_code?: string | null
    parent_dr_id: number | null
    dr_relation?: "child" | "parent" | "sibling" | null
    dr_id?: number | string | null
}

export type DispatchRequestOtherTicketsResponse = {
    ticket_id: number
    total: number
    tickets: DispatchRequestOtherTicket[]
}

export type DispatchRequestAllocationBatch = {
    inventory_id: number
    batch_id: string | null
    available: number
}

export type DispatchRequestAllocationSource = {
    site_id: number
    site_name: string
    city?: string
    state?: string
    gst_code?: string
    atp: number
    cins?: string[]
    batches?: DispatchRequestAllocationBatch[]
}

export type DispatchRequestAllocationSourcesResponse = {
    logistics_item_id: number
    product_id: number
    sku_code: string
    track: "serial" | "batch" | null
    requested_qty: number
    allocated_qty: number
    sources: DispatchRequestAllocationSource[]
}

export type BulkAllocationSkuCoverage = {
    product_id: number
    sku_code: string
    display_name?: string
    requested_qty: number
    atp: number
    short_qty: number
    covers: boolean
}

export type BulkAllocationWarehouse = {
    site_id: number
    site_name: string
    city?: string | null
    state?: string | null
    disabled: boolean
    covers_all: boolean
    skus_covered: number
    skus_total: number
    by_sku: BulkAllocationSkuCoverage[]
}

export type BulkAllocationSkippedLine = {
    logistics_item_id: number
    sku_code?: string
    reason: string
}

export type BulkAllocationSkuSummary = {
    product_id: number
    sku_code: string
    display_name?: string
    requested_qty: number
    line_ids: number[]
}

export type BulkAllocationSourcesResponse = {
    dr_id: number
    lines: number[]
    skipped: BulkAllocationSkippedLine[]
    skus: BulkAllocationSkuSummary[]
    warehouses: BulkAllocationWarehouse[]
}

export type BulkAllocationPlanPayload = {
    dr_id: number
    logistics_item_ids: number[]
    source_site_id: number
}

export type BulkAllocationPlanLine = {
    logistics_item_id: number
    sku_code: string
    requested_qty: number
    allocatable_qty: number
    short_qty: number
    cins: string[] | null
    allocations?: (BatchAllocationPayload & { batch_id?: string | null })[]
    status: "allocated" | "allocated_partial" | "no_stock"
}

export type BulkAllocationPlanSummary = {
    selected: number
    fully_allocated: number
    partial: { sku_code: string; allocatable_qty: number; requested_qty: number }[]
    no_stock: string[]
    skipped: number
}

export type BulkAllocationPlanResponse = {
    dr_id: number
    source_site_id: number
    source_site_name: string
    lines: BulkAllocationPlanLine[]
    items: CommitSelectionWarehouseItem[]
    skipped: BulkAllocationSkippedLine[]
    summary: BulkAllocationPlanSummary
}

export type BatchAllocationPayload = {
    inventory_id: number
    quantity: number
}

export type CommitSelectionWarehouseItem = {
    logistics_item_id: number
    selected: boolean
    source_site_id?: number
    quantity?: number
    cins?: string[]
    allocations?: BatchAllocationPayload[]
    remarks?: string
}

export type CommitSelectionDropShipItem = {
    logistics_item_id: number
    dr_id: number
    alloc_method: "bill_to_ship_to"
    payable_po_id: number
    vendor_id: number
    vendor: string
    logistics_by: "statiq" | "vendor"
    allocated_qty: number
    line_status: string
    remarks?: string
}

export type CommitSelectionItemPayload = CommitSelectionWarehouseItem | CommitSelectionDropShipItem

export type CommitSelectionPayload = {
    dr_id: number
    user_id: number
    items: CommitSelectionItemPayload[]
}

export type CommitSelectionResponse = {
    dr_id: number
    dr_status: string
    child_dr_id: number | null
    items: { logistics_item_id: number; source: { site_id: number }; line_status: string }[]
    item_errors: { logistics_item_id: number; message: string }[]
    finance_ticket_ids: number[]
}

export type SubmitForApprovalPayload = {
    dr_id: number
    user_id: number
    approver_user_ids: number[]
    items: CommitSelectionItemPayload[]
}

export type SubmitForApprovalResponse = {
    dr_id: number
    dr_status: string
    approval_id: number
    lines: { logistics_item_id: number; line_status: string }[]
}

export type DispatchRequestApprovalApprover = {
    name: string
    action: string | null
    reason: string | null
    sequence: number
    acted_at: string | null
}
export type DispatchRequestApprovalLine = {
    logistics_item_id: number
    sku_code: string
    product_name: string
    quantity: number
    source_site_id: number | null
    line_status: string
}
export type DispatchRequestApproval = {
    id: number
    name: string
    type: string
    status: string
    remarks: string | null
    raised_on: string
    raised_by: string | null
    lines: DispatchRequestApprovalLine[]
    approvers: DispatchRequestApprovalApprover[]
}
export type DispatchRequestApprovalsResponse = {
    dr_id: number
    approvals: DispatchRequestApproval[]
}

export type MarkAllocatedPayload = { dr_id: number; user_id: number }

export type MarkAllocatedResponse = {
    dr_id: number
    dr_status: string
    child_dr_id: number | null
    logistics_item_ids: number[]
    finance_ticket_ids: number[]
}

export type DispatchRequestCinOptionsResponse = {
    logistics_item_id: number
    product_id: number
    sku_code: string
    source_site_id: number
    current_cin: string
    available_cins: string[]
}

export type EditDispatchRequestMaterialPayload = {
    logistics_item_id: number
    user_id: number
    sku_code?: string
    quantity?: number
    branding_id?: number
    remarks?: string
}

export type EditDispatchRequestMaterialResponse = {
    logistics_item_id: number
    dr_id: number
    product: { id: number; sku_code: string }
    quantity: number
    branding_id: number | null
    remarks: string | null
}

export type ChangeCinPayload = {
    logistics_item_id: number
    user_id: number
    new_cin: string
}

export type ChangeCinResponse = {
    logistics_item_id: number
    dr_id: number
    old_cin: string
    new_cin: string
    source_site_id: number
}

export type UpdateDispatchRequestTicketPayload = {
    ticket_id: string | number
    user_id?: number
    requested_delivery_date?: string
    requested_delivery_time?: number
    source_site_id?: number
    destination_site_id?: number
    assigned_to?: number
    source_poc?: DispatchRequestPocPayload
    destination_poc?: DispatchRequestPocPayload
}

export type UpdateDispatchRequestTicketResponse = {
    message?: string
}

export type SetDropShipAllocation = { po_item_id: number; quantity: number }

export type SetDropShipPayload = {
    logistics_item_id: number
    allocations: SetDropShipAllocation[]
    logistics_by?: "statiq" | "vendor"
    source_site_id?: number
    user_id: number
}

export type SetDropShipRow = {
    logistics_item_id: number
    po_item_id: number
    payable_po_id: number
    po_number: string
    quantity: number
    allocated_qty: number
    rate: number | null
}

export type SetDropShipResponse = {
    logistics_item_id: number
    dr_id: number
    alloc_method: "bill_to_ship_to"
    vendor_id: number
    vendor: string
    logistics_by: "statiq" | "vendor"
    source_site_id: number | null
    allocated_qty: number
    line_status: string
    rows: SetDropShipRow[]
    leftover: { logistics_item_id: number; quantity: number } | null
}

export type DispatchRequestPayablePoItem = {
    po_item_id: number
    item_qty: number
    committed_qty: number
    remaining: number
    rate: number | null
}

export type DispatchRequestPayablePo = {
    id: number
    po_number: string
    vendor_id: number
    vendor_name: string
    po_date?: string | null
    status?: string
    items: DispatchRequestPayablePoItem[]
}

export const DEALLOCATION_REASONS = [
    "Customer cancelled",
    "Mis-allocation",
    "Wrong CIN selected",
    "Credit hold",
    "Finance bounced the ticket",
    "Stock correction",
] as const
export type DeallocationReason = (typeof DEALLOCATION_REASONS)[number]

export type DeallocateItemPayload = {
    logistics_item_id: number
    user_id: number
    reason: DeallocationReason
}

export type DeallocateItemResponse = {
    logistics_item_id: number
    dr_id: number
    released_qty: number
    released_cin: string | null
    line_status: string
}

export type UploadDrMaterialPayload = {
    product_id: number
    quantity?: number
    remarks?: string
    scope?: string
}

export type UploadDrPayload = {
    user_id: number
    dr_id?: number
    create_new_ticket?: boolean
    project_id?: number
    source_site_id?: number
    destination_site_id?: number
    visit_by_date?: string
    visit_by_time?: number
    deal_type?: string
    template_id?: number
    is_reverse?: boolean
    reverse_from_project_id?: number
    demo_ticket_id?: number
    source?: string
    materials?: UploadDrMaterialPayload[]
    file?: File
}

export type UploadDrResponse = {
    msg: string
    dr_id: number
    dr: { file_name: string; file_url: string }
}

export type DispatchRequestParsedItem = {
    sku_code: string
    description?: string
    quantity: number
    remarks?: string
    scope?: string
}

export type DispatchRequestParsedGroup = {
    dr_id: number
    dr_display_id: string
    items: DispatchRequestParsedItem[]
}

export type DispatchRequestParsedDrParams = {
    project_id?: number
    dr_id?: number
    response_type?: "ticket_wise" | "total"
}

export type DispatchRequestBoqItem = {
    sku_code?: string
    description?: string
    product_id?: number
    total_quantity: number
    issued_quantity: number
    remaining_quantity: number
    logistics_item_id?: number
}

export type DispatchRequestBoqParams = {
    project_id: number
    dr_id?: number
    boq_type?: string
    source?: string
    status?: string
}

export type CheckDrCreationStatusParams = {
    project_id: number
    type: "normal" | "reverse" | "demolition"
    source_site_id?: number
    destination_site_id?: number
    ticket_id?: number
}

export type CheckDrCreationStatusResponse = {
    create_new_ticket: boolean
    replace: { flag: boolean; message?: string; dr_id?: number }
    delivery_requests?: any[]
    ticket_document_map?: Record<string, any>
}

export type DispatchRequestMaterialsParams = {
    dr_id: number
    response_level?: "sku_level" | "item_level"
}

export type DispatchRequestMaterialSkuRow = {
    product: { id: number; sku_code: string; track: "serial" | "batch" | null; type?: "good" | "service" }
    requested_qty: number
    allocated_qty: number
    sources: { site_id: number }[]
    cins: string[] | null
    serial_batch?: string | null
    line_status: string
    logistics_item_ids: number[]
    is_service?: boolean
}

export type DispatchRequestMaterialsResponse = DispatchRequestMaterialSkuRow[] | any[]

type DispatchRequestMaterialsRawResponse = {
    display_id?: string
    dr_id?: number
    materials: DispatchRequestMaterialsResponse
    response_level?: "sku_level" | "item_level"
}

const handleSnackbar = async (queryFulfilled: any, successMsg: (data: any) => string, fallback = "Success") => {
    try {
        const { data } = await queryFulfilled
        enqueueSnackbar(successMsg(data) || fallback, { variant: "success" })
    } catch (error: any) {
        enqueueSnackbar(
            error?.error?.data?.message || error?.error?.data?.error || error?.message || "Something went wrong",
            {
                variant: "error",
            }
        )
    }
}

export type { LogisticsTabData as DispatchRequestTabData }

const dispatchRequestService = api.injectEndpoints({
    endpoints: build => ({
        getDispatchRequestTabs: build.query({
            query: (params: { data: any; page: number; selectedValue: number }) => ({
                url: `${MODULE_URL}/total`,
                params: {
                    status_ids: JSON.stringify(params.data.statusId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    created_start: params.data.TicketStartDate ?? "",
                    created_end: params.data.TicketEndDate ?? "",
                    product_ids: JSON.stringify(
                        (params.data.product_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    search_key: params.data.search_key ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    ticket_type: params.data.ticketType ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                    requested_delivery_date_from: params.data.requested_delivery_date_from ?? "",
                    requested_delivery_date_to: params.data.requested_delivery_date_to ?? "",
                    on_hold: params.data.on_hold?.id === 1 ? "true" : params.data.on_hold?.id === 0 ? "false" : "",
                    on_hold_date: params.data.on_hold_date ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    template_id: params.data.template_id?.id ?? params.data.template_id ?? "",
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    destination_site_ids: JSON.stringify(
                        (params.data.destination_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                },
                useV3: true,
            }),
            transformResponse: (response: LogisticsTabsResponse) => response?.data?.message,
            providesTags: ["DispatchRequest"],
        }),
        getDispatchRequestData: build.query<TicketDataResponseType, { data: any; selectedValue: number; page: number }>(
            {
                query: params => {
                    const queryParams = {
                        assigned_to: params.data.assigned_to?.id ?? "",
                        status_ids: JSON.stringify(params.data.statusId ?? []),
                        tag_ids: JSON.stringify(params.data.tags ?? []),
                        age: JSON.stringify(params.data.age ?? []),
                        age_op: params.data.age_op ?? "",
                        created_start: params.data.TicketStartDate ?? "",
                        created_end: params.data.TicketEndDate ?? "",
                        product_ids: JSON.stringify(
                            (params.data.product_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                        ),
                        search_key: params.data.search_key ?? "",
                        read_filter: params.data.ReadUnread ?? "all",
                        ticket_type: params.data.ticketType ?? "all",
                        per_page: params?.selectedValue,
                        page: params?.page,
                        queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                        requested_delivery_date_from: params.data.requested_delivery_date_from ?? "",
                        requested_delivery_date_to: params.data.requested_delivery_date_to ?? "",
                        on_hold: params.data.on_hold?.id === 1 ? "true" : params.data.on_hold?.id === 0 ? "false" : "",
                        on_hold_date: params.data.on_hold_date ?? "",
                        on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                        template_id: params.data.template_id?.id ?? params.data.template_id ?? "",
                        category_ids: JSON.stringify(
                            (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                        ),
                        destination_site_ids: JSON.stringify(
                            (params.data.destination_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                        ),
                    }

                    return {
                        url: `${MODULE_URL}/${params.data.parent_status ?? "all"}`,
                        params: queryParams,
                        useV3: true,
                    }
                },
                providesTags: ["DispatchRequest"],
            }
        ),
        getDispatchRequestTicketDetails: build.query<
            DispatchRequestDetailResponse,
            { ticket_id: string; user_id: number }
        >({
            query: ({ ticket_id, user_id }) => ({
                url: `${MODULE_URL}/details`,
                params: { ticket_id, user_id },
                useV3: true,
            }),
            providesTags: (result, error, args) => [{ type: "DispatchRequest", id: args.ticket_id }],
        }),
        getDispatchRequestTicketHistory: build.query<HistoryData[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/history`,
                params: { dr_id: ticket_id },
                useV3: true,
            }),
            providesTags: (result, error, args) => [{ type: "DispatchRequest", id: args.ticket_id }],
        }),
        getDispatchRequestOtherTickets: build.query<DispatchRequestOtherTicketsResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/tickets`,
                useV3: true,
            }),
            providesTags: (result, error, args) => [{ type: "DispatchRequest", id: args.ticket_id }],
        }),
        downloadDispatchRequests: build.query<DispatchRequestDownloadResponse, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download`,
                params: {
                    status_ids: JSON.stringify(params.data.statusId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    created_start: params.data.TicketStartDate ?? "",
                    created_end: params.data.TicketEndDate ?? "",
                    product_ids: JSON.stringify(
                        (params.data.product_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    search_key: params.data.search_key ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    ticket_type: params.data.ticketType ?? "all",
                    queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                    requested_delivery_date_from: params.data.requested_delivery_date_from ?? "",
                    requested_delivery_date_to: params.data.requested_delivery_date_to ?? "",
                    on_hold: params.data.on_hold?.id === 1 ? "true" : params.data.on_hold?.id === 0 ? "false" : "",
                    on_hold_date: params.data.on_hold_date ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    template_id: params.data.template_id?.id ?? params.data.template_id ?? "",
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    destination_site_ids: JSON.stringify(
                        (params.data.destination_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    parent_status: params.data.parent_status ?? "all",
                    download_type: "list",
                },
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.message || "File Download Successfully")
            },
            keepUnusedDataFor: 0,
        }),
        raiseDispatchRequestSolo: build.mutation<RaiseDispatchRequestSoloResponse, RaiseDispatchRequestSoloPayload>({
            query: payload => ({
                url: `${MODULE_URL}/raise_solo`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: ["DispatchRequest"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, () => "Dispatch request created successfully")
            },
        }),
        getDispatchRequestAllocationSources: build.query<
            DispatchRequestAllocationSourcesResponse,
            { dr_item_id: number }
        >({
            query: ({ dr_item_id }) => ({
                url: `${MODULE_URL}/allocation_sources`,
                params: { dr_item_id },
                useV3: true,
            }),
            providesTags: ["DispatchRequest"],
        }),

        getDispatchRequestBulkAllocationSources: build.query<
            BulkAllocationSourcesResponse,
            { dr_id: number; logistics_item_ids: number[] }
        >({
            query: ({ dr_id, logistics_item_ids }) => ({
                url: `${MODULE_URL}/bulk_allocation_sources`,
                method: "POST",
                body: { dr_id, logistics_item_ids },
                useV3: true,
            }),
        }),

        planDispatchRequestBulkAllocation: build.mutation<BulkAllocationPlanResponse, BulkAllocationPlanPayload>({
            query: body => ({
                url: `${MODULE_URL}/bulk_allocation_plan`,
                method: "POST",
                body,
                useV3: true,
            }),
        }),

        commitDispatchRequestSelection: build.mutation<CommitSelectionResponse, CommitSelectionPayload>({
            query: payload => ({
                url: `${MODULE_URL}/commit_selection`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: (result, error, args) => [
                { type: "DispatchRequest", id: String(args.dr_id) },
                "DispatchRequest",
            ],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, data =>
                    data?.child_dr_id
                        ? `Allocation committed — remaining lines moved to child DR ${data.child_dr_id}.`
                        : "Allocation committed."
                )
            },
        }),
        submitDispatchRequestForApproval: build.mutation<SubmitForApprovalResponse, SubmitForApprovalPayload>({
            query: payload => ({
                url: `${MODULE_URL}/submit_for_approval`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: (result, error, args) => [
                { type: "DispatchRequest", id: String(args.dr_id) },
                "DispatchRequest",
            ],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, data => `Submitted for approval — approval #${data.approval_id}.`)
            },
        }),
        deallocateDispatchRequestItem: build.mutation<DeallocateItemResponse, DeallocateItemPayload>({
            query: payload => ({
                url: `${MODULE_URL}/deallocate_item`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: ["DispatchRequest"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, () => "Line deallocated.")
            },
        }),
        getDispatchRequestMaterials: build.query<DispatchRequestMaterialsResponse, DispatchRequestMaterialsParams>({
            query: params => ({
                url: `${MODULE_URL}/materials`,
                params,
                useV3: true,
            }),
            transformResponse: (response: DispatchRequestMaterialsRawResponse) => response?.materials ?? [],
            providesTags: (result, error, args) => [{ type: "DispatchRequest", id: args.dr_id }],
        }),
        getDispatchRequestCinOptions: build.query<DispatchRequestCinOptionsResponse, { logistics_item_id: number }>({
            query: params => ({
                url: `${MODULE_URL}/cin_options`,
                params,
                useV3: true,
            }),
        }),
        changeDispatchRequestCin: build.mutation<ChangeCinResponse, ChangeCinPayload>({
            query: payload => ({
                url: `${MODULE_URL}/change_cin`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: ["DispatchRequest"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, data => `CIN swapped — ${data.old_cin} → ${data.new_cin}.`)
            },
        }),
        editDispatchRequestMaterial: build.mutation<
            EditDispatchRequestMaterialResponse,
            EditDispatchRequestMaterialPayload
        >({
            query: ({ logistics_item_id, ...body }) => ({
                url: `${MODULE_URL}/material/${logistics_item_id}`,
                method: "PUT",
                body,
                useV3: true,
            }),
            transformResponse: (response: { data: EditDispatchRequestMaterialResponse }) => response.data,
        }),
        updateDispatchRequestTicket: build.mutation<
            UpdateDispatchRequestTicketResponse,
            UpdateDispatchRequestTicketPayload
        >({
            query: payload => ({
                url: `${MODULE_URL}/update_ticket`,
                method: "PUT",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: ["DispatchRequest"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, data => data.message || "", "Ticket updated.")
            },
        }),
        getDispatchRequestDrApprovals: build.query<DispatchRequestApprovalsResponse, { dr_id: number }>({
            query: params => ({
                url: `${MODULE_URL}/approvals`,
                params,
                useV3: true,
            }),
            providesTags: (result, error, args) => [{ type: "DispatchRequest", id: args.dr_id }],
        }),
        markDispatchRequestAllocated: build.mutation<MarkAllocatedResponse, MarkAllocatedPayload>({
            query: payload => ({
                url: `${MODULE_URL}/mark_allocated`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: (result, error, args) => [
                { type: "DispatchRequest", id: String(args.dr_id) },
                "DispatchRequest",
            ],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, data =>
                    data?.child_dr_id
                        ? `Marked as allocated - finance ticket(s) created. Remaining lines moved to child DR ${data.child_dr_id}.`
                        : "Marked as allocated - finance ticket(s) created."
                )
            },
        }),
        setDispatchRequestDropShip: build.mutation<SetDropShipResponse, SetDropShipPayload>({
            query: payload => ({
                url: `${MODULE_URL}/set_drop_ship`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: ["DispatchRequest"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, data =>
                    data.leftover
                        ? `Drop-ship set — ${data.leftover.quantity} unit(s) left unallocated.`
                        : "Drop-ship set — vendor ships direct to site."
                )
            },
        }),
        getDispatchRequestPayablePos: build.query<
            DispatchRequestPayablePo[],
            { vendor_id?: number; logistics_item_id?: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/payable_pos`,
                params,
                useV3: true,
            }),
            transformResponse: (response: { purchase_orders: DispatchRequestPayablePo[] }) =>
                response?.purchase_orders ?? [],
        }),
        markDispatchRequestRead: build.mutation<{ message: string }, { dr_id: number; user_id: number }>({
            query: payload => ({
                url: `${MODULE_URL}/mark_read`,
                method: "POST",
                body: payload,
                useV3: true,
            }),
            invalidatesTags: ["DispatchRequest"],
        }),
    }),
})

export const {
    useLazyGetDispatchRequestTabsQuery,
    useLazyGetDispatchRequestDataQuery,
    useGetDispatchRequestTicketDetailsQuery,
    useGetDispatchRequestTicketHistoryQuery,
    useGetDispatchRequestOtherTicketsQuery,
    useLazyDownloadDispatchRequestsQuery,
    useRaiseDispatchRequestSoloMutation,
    useGetDispatchRequestAllocationSourcesQuery,
    useLazyGetDispatchRequestAllocationSourcesQuery,
    useLazyGetDispatchRequestBulkAllocationSourcesQuery,
    usePlanDispatchRequestBulkAllocationMutation,
    useCommitDispatchRequestSelectionMutation,
    useSubmitDispatchRequestForApprovalMutation,
    useDeallocateDispatchRequestItemMutation,
    useGetDispatchRequestCinOptionsQuery,
    useChangeDispatchRequestCinMutation,
    useEditDispatchRequestMaterialMutation,
    useUpdateDispatchRequestTicketMutation,
    useGetDispatchRequestMaterialsQuery,
    useGetDispatchRequestDrApprovalsQuery,
    useMarkDispatchRequestAllocatedMutation,
    useSetDispatchRequestDropShipMutation,
    useGetDispatchRequestPayablePosQuery,
    useMarkDispatchRequestReadMutation,
} = dispatchRequestService

export { dispatchRequestService }
